export type PdfFallbackTextItem = {
  text: string;
  pageNum?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  [key: string]: unknown;
};

export type PdfFallbackPageItemsByPage = Record<string, PdfFallbackTextItem[]>;

export type PdfFallbackBoundary = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PdfFallbackDimensionItem = PdfFallbackBoundary & {
  items: PdfFallbackTextItem[];
  pageNumber: number;
};

export interface PdfFallbackSearchResult {
  boundary: PdfFallbackBoundary | null;
  dimensionItems: PdfFallbackDimensionItem[];
  matchedItems: PdfFallbackTextItem[];
  pageNumber: number | null;
  pageNumbers: number[];
}

type MatchResult<T> = {
  item: T;
  score: number;
  matchedText?: string;
  normalizedNeedle: string;
};

type MatchCombinationResult<T> = {
  matches: MatchResult<T>[];
  combinedText: string;
  normalizedCombinedText: string;
  normalizedTargetText: string;
  score: number;
  exact: boolean;
};

type FuzzySearchOptions<T> = {
  getText: (item: T) => string;
  threshold?: number;
  minLength?: number;
  pageNum?: number;
  getPageNum?: (item: T) => number | undefined;
};

type MatchCombinationOptions<T> = {
  getText: (item: T) => string;
  getPageNum?: (item: T) => number | undefined;
  getY?: (item: T) => number | undefined;
  getX?: (item: T) => number | undefined;
  lineTolerance?: number;
};

type AnchoredMatch<T> = {
  match: MatchResult<T>;
  start: number;
  end: number;
  text: string;
};

type MatchCombinationState<T> = {
  anchoredMatches: AnchoredMatch<T>[];
  coveredChars: number;
  totalMatchScore: number;
  firstStart: number | null;
  lastEnd: number;
};

const lineTolerance = 2;

function normalizePageNumbers(pageNumbers: Array<number | null | undefined>) {
  return Array.from(
    new Set(
      pageNumbers.filter(
        (pageNumber): pageNumber is number =>
          typeof pageNumber === 'number' &&
          Number.isInteger(pageNumber) &&
          pageNumber > 0
      )
    )
  ).sort((leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber);
}

function toPositivePageNumber(value: unknown): number | null {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value > 0
    ? value
    : null;
}

export function groupPdfFallbackTextItemsByPage(
  value: unknown
): PdfFallbackPageItemsByPage {
  const nextItemsByPage: PdfFallbackPageItemsByPage = {};

  const addItem = (
    item: PdfFallbackTextItem,
    fallbackPageNumber?: number | null
  ) => {
    const resolvedPageNumber =
      toPositivePageNumber(item.pageNum) ?? fallbackPageNumber ?? null;
    const pageKey =
      resolvedPageNumber === null ? 'unknown' : String(resolvedPageNumber);
    const normalizedItem =
      resolvedPageNumber === null
        ? item
        : {
            ...item,
            pageNum: resolvedPageNumber
          };

    if (!nextItemsByPage[pageKey]) {
      nextItemsByPage[pageKey] = [];
    }

    nextItemsByPage[pageKey].push(normalizedItem);
  };

  if (Array.isArray(value)) {
    for (const item of value) {
      if (item && typeof item === 'object') {
        addItem(item as PdfFallbackTextItem);
      }
    }

    return nextItemsByPage;
  }

  if (!value || typeof value !== 'object') {
    return nextItemsByPage;
  }

  for (const [pageKey, pageItems] of Object.entries(
    value as Record<string, unknown>
  )) {
    if (!Array.isArray(pageItems)) {
      continue;
    }

    const fallbackPageNumber = toPositivePageNumber(Number(pageKey));

    for (const item of pageItems) {
      if (item && typeof item === 'object') {
        addItem(item as PdfFallbackTextItem, fallbackPageNumber);
      }
    }
  }

  return nextItemsByPage;
}

export function flattenPdfFallbackTextItemsByPage(
  itemsByPage: PdfFallbackPageItemsByPage,
  pageNumbers?: number[]
): PdfFallbackTextItem[] {
  const resolvedPageNumbers = normalizePageNumbers(pageNumbers ?? []);

  if (resolvedPageNumbers.length > 0) {
    return resolvedPageNumbers.flatMap(
      (pageNumber) => itemsByPage[String(pageNumber)] ?? []
    );
  }

  return Object.entries(itemsByPage)
    .sort(([leftPageKey], [rightPageKey]) => {
      if (leftPageKey === 'unknown') {
        return 1;
      }

      if (rightPageKey === 'unknown') {
        return -1;
      }

      return Number(leftPageKey) - Number(rightPageKey);
    })
    .flatMap(([, pageItems]) => pageItems);
}

function normalizeForSearch(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
    .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeLoose(input: string): string {
  return normalizeForSearch(input)
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeForAlignment(input: string): string {
  return normalizeForSearch(input);
}

function diceCoefficient(a: string, b: string): number {
  if (a === b) {
    return 1;
  }

  if (a.length < 2 || b.length < 2) {
    return 0;
  }

  const bigrams = new Map<string, number>();

  for (let index = 0; index < a.length - 1; index++) {
    const bigram = a.slice(index, index + 2);
    bigrams.set(bigram, (bigrams.get(bigram) ?? 0) + 1);
  }

  let intersection = 0;

  for (let index = 0; index < b.length - 1; index++) {
    const bigram = b.slice(index, index + 2);
    const count = bigrams.get(bigram) ?? 0;

    if (count > 0) {
      bigrams.set(bigram, count - 1);
      intersection += 1;
    }
  }

  return (2 * intersection) / (a.length + b.length - 2);
}

function getWindows(text: string, targetLength: number): string[] {
  const words = text.split(' ').filter(Boolean);
  const targetWords = Math.max(1, targetLength);
  const windows: string[] = [];

  for (let index = 0; index < words.length; index++) {
    const window = words.slice(index, index + targetWords).join(' ');

    if (window) {
      windows.push(window);
    }
  }

  return windows;
}

function joinTextFragments(parts: string[]): string {
  let combined = '';

  for (const part of parts) {
    const trimmed = part.trim();

    if (!trimmed) {
      continue;
    }

    if (!combined) {
      combined = trimmed;
      continue;
    }

    if (
      /^[,.;:!?%)\]}]/.test(trimmed) ||
      /[-/({]$/.test(combined) ||
      combined.endsWith('[')
    ) {
      combined += trimmed;
      continue;
    }

    combined += ` ${trimmed}`;
  }

  return combined;
}

function getNumericValue(value: number | undefined, fallback = 0): number {
  return typeof value === 'number' ? value : fallback;
}

function findAllOccurrences(text: string, fragment: string): number[] {
  if (!fragment) {
    return [];
  }

  const indexes: number[] = [];
  let fromIndex = 0;

  while (fromIndex < text.length) {
    const foundIndex = text.indexOf(fragment, fromIndex);

    if (foundIndex === -1) {
      break;
    }

    indexes.push(foundIndex);
    fromIndex = foundIndex + 1;
  }

  return indexes;
}

function findObjectsInLargerString<T>(
  items: T[],
  largerString: string,
  options: FuzzySearchOptions<T>
): MatchResult<T>[] {
  const {
    getText,
    threshold = 0.82,
    minLength = 3,
    pageNum,
    getPageNum
  } = options;
  const normalizedLargeStrict = normalizeForSearch(largerString);
  const normalizedLargeLoose = normalizeLoose(largerString);
  const results: MatchResult<T>[] = [];

  for (const item of items) {
    const itemPageNum =
      getPageNum?.(item) ?? (item as { pageNum?: number }).pageNum;

    if (pageNum !== undefined && itemPageNum !== pageNum) {
      continue;
    }

    const rawNeedle = getText(item);

    if (!rawNeedle || rawNeedle.trim().length < minLength) {
      continue;
    }

    const needleStrict = normalizeForSearch(rawNeedle);
    const needleLoose = normalizeLoose(rawNeedle);

    if (!needleLoose || needleLoose.length < minLength) {
      continue;
    }

    if (normalizedLargeStrict.includes(needleStrict)) {
      results.push({
        item,
        matchedText: rawNeedle,
        normalizedNeedle: needleStrict,
        score: 1
      });
      continue;
    }

    if (normalizedLargeLoose.includes(needleLoose)) {
      results.push({
        item,
        matchedText: rawNeedle,
        normalizedNeedle: needleLoose,
        score: 0.96
      });
      continue;
    }

    const needleWordCount = needleLoose.split(' ').length;
    const candidateWindowSizes = [
      Math.max(1, needleWordCount - 2),
      Math.max(1, needleWordCount - 1),
      needleWordCount,
      needleWordCount + 1,
      needleWordCount + 2
    ];

    let bestScore = 0;
    let bestWindow = '';

    for (const windowSize of candidateWindowSizes) {
      const windows = getWindows(normalizedLargeLoose, windowSize);

      for (const window of windows) {
        const score = diceCoefficient(needleLoose, window);

        if (score > bestScore) {
          bestScore = score;
          bestWindow = window;
        }
      }
    }

    if (bestScore >= threshold) {
      results.push({
        item,
        matchedText: bestWindow,
        normalizedNeedle: needleLoose,
        score: bestScore
      });
    }
  }

  return results.sort((left, right) => right.score - left.score);
}

function findBestMatchCombination<T>(
  matches: MatchResult<T>[],
  largerString: string,
  options: MatchCombinationOptions<T>
): MatchCombinationResult<T> | null {
  const { getText, getPageNum, getY, getX, lineTolerance = 2 } = options;
  const normalizedTargetText = normalizeForSearch(largerString);

  if (!normalizedTargetText) {
    return null;
  }

  const orderedMatches = [...matches].sort((left, right) => {
    const leftPage =
      getPageNum?.(left.item) ?? (left.item as { pageNum?: number }).pageNum;
    const rightPage =
      getPageNum?.(right.item) ?? (right.item as { pageNum?: number }).pageNum;
    const pageDiff = getNumericValue(leftPage) - getNumericValue(rightPage);

    if (pageDiff !== 0) {
      return pageDiff;
    }

    const leftY = getY?.(left.item) ?? (left.item as { y?: number }).y;
    const rightY = getY?.(right.item) ?? (right.item as { y?: number }).y;
    const yDiff = getNumericValue(leftY) - getNumericValue(rightY);

    if (Math.abs(yDiff) > lineTolerance) {
      return yDiff;
    }

    const leftX = getX?.(left.item) ?? (left.item as { x?: number }).x;
    const rightX = getX?.(right.item) ?? (right.item as { x?: number }).x;

    return getNumericValue(leftX) - getNumericValue(rightX);
  });

  const normalizedMatchText = orderedMatches.map((match) =>
    normalizeForSearch(getText(match.item))
  );
  const occurrenceCache = new Map<string, number[]>();

  const getOccurrences = (fragment: string): number[] => {
    const cached = occurrenceCache.get(fragment);

    if (cached) {
      return cached;
    }

    const occurrences = findAllOccurrences(normalizedTargetText, fragment);
    occurrenceCache.set(fragment, occurrences);
    return occurrences;
  };

  const buildResultFromState = (
    state: MatchCombinationState<T> | null
  ): MatchCombinationResult<T> | null => {
    if (
      !state ||
      state.anchoredMatches.length === 0 ||
      state.firstStart === null
    ) {
      return null;
    }

    const exact =
      state.firstStart === 0 && state.lastEnd === normalizedTargetText.length;
    const averageMatchScore =
      state.totalMatchScore / state.anchoredMatches.length;
    const spanCoverage =
      normalizedTargetText.length === 0
        ? 0
        : (state.lastEnd - state.firstStart) / normalizedTargetText.length;
    const anchoredCoverage =
      normalizedTargetText.length === 0
        ? 0
        : state.coveredChars / normalizedTargetText.length;

    return {
      combinedText: exact
        ? largerString
        : joinTextFragments(
            state.anchoredMatches.map((anchoredMatch) => anchoredMatch.text)
          ),
      exact,
      matches: state.anchoredMatches.map(
        (anchoredMatch) => anchoredMatch.match
      ),
      normalizedCombinedText: exact
        ? normalizedTargetText
        : normalizedTargetText.slice(state.firstStart, state.lastEnd),
      normalizedTargetText,
      score: exact
        ? 1
        : Math.min(
            0.99,
            (spanCoverage * 0.7 + anchoredCoverage * 0.3) * averageMatchScore
          )
    };
  };

  const isBetterState = (
    candidate: MatchCombinationState<T> | null,
    current: MatchCombinationState<T> | null
  ): boolean => {
    const candidateResult = buildResultFromState(candidate);
    const currentResult = buildResultFromState(current);

    if (!candidateResult) {
      return false;
    }

    if (!currentResult) {
      return true;
    }

    if (candidateResult.exact !== currentResult.exact) {
      return candidateResult.exact;
    }

    const candidateStartsAtBeginning = candidate?.firstStart === 0;
    const currentStartsAtBeginning = current?.firstStart === 0;

    if (candidateStartsAtBeginning !== currentStartsAtBeginning) {
      return candidateStartsAtBeginning;
    }

    const candidateEndsAtTargetEnd =
      candidate?.lastEnd === normalizedTargetText.length;
    const currentEndsAtTargetEnd =
      current?.lastEnd === normalizedTargetText.length;

    if (candidateEndsAtTargetEnd !== currentEndsAtTargetEnd) {
      return candidateEndsAtTargetEnd;
    }

    const candidateSpan =
      candidate && candidate.firstStart !== null
        ? candidate.lastEnd - candidate.firstStart
        : 0;
    const currentSpan =
      current && current.firstStart !== null
        ? current.lastEnd - current.firstStart
        : 0;

    if (candidateSpan !== currentSpan) {
      return candidateSpan > currentSpan;
    }

    if ((candidate?.coveredChars ?? 0) !== (current?.coveredChars ?? 0)) {
      return (candidate?.coveredChars ?? 0) > (current?.coveredChars ?? 0);
    }

    if (candidateResult.score !== currentResult.score) {
      return candidateResult.score > currentResult.score;
    }

    return candidateResult.matches.length > currentResult.matches.length;
  };

  const memo = new Map<string, MatchCombinationState<T> | null>();

  const search = (
    startIndex: number,
    minTargetIndex: number
  ): MatchCombinationState<T> | null => {
    const memoKey = `${startIndex}:${minTargetIndex}`;
    const cached = memo.get(memoKey);

    if (cached !== undefined) {
      return cached;
    }

    let bestState: MatchCombinationState<T> | null = null;

    for (let index = startIndex; index < orderedMatches.length; index++) {
      const fragment = normalizedMatchText[index];

      if (!fragment) {
        continue;
      }

      const occurrences = getOccurrences(fragment).filter(
        (occurrence) => occurrence >= minTargetIndex
      );

      for (const occurrenceStart of occurrences) {
        const occurrenceEnd = occurrenceStart + fragment.length;
        const suffixState = search(index + 1, occurrenceEnd);

        const anchoredMatch: AnchoredMatch<T> = {
          end: occurrenceEnd,
          match: orderedMatches[index],
          start: occurrenceStart,
          text: getText(orderedMatches[index].item)
        };

        const candidateState: MatchCombinationState<T> = {
          anchoredMatches: [
            anchoredMatch,
            ...(suffixState?.anchoredMatches ?? [])
          ],
          coveredChars: fragment.length + (suffixState?.coveredChars ?? 0),
          firstStart: occurrenceStart,
          lastEnd: suffixState?.lastEnd ?? occurrenceEnd,
          totalMatchScore:
            orderedMatches[index].score + (suffixState?.totalMatchScore ?? 0)
        };

        if (isBetterState(candidateState, bestState)) {
          bestState = candidateState;
        }
      }
    }

    memo.set(memoKey, bestState);
    return bestState;
  };

  return buildResultFromState(search(0, 0));
}

function getItemKey(item: PdfFallbackTextItem): string {
  return [
    item.pageNum ?? '',
    item.text ?? '',
    item.x ?? '',
    item.y ?? '',
    item.width ?? '',
    item.height ?? ''
  ].join('|');
}

function sortTextItemsReadingOrder(
  items: PdfFallbackTextItem[]
): PdfFallbackTextItem[] {
  return [...items].sort((left, right) => {
    const leftPage = typeof left.pageNum === 'number' ? left.pageNum : 0;
    const rightPage = typeof right.pageNum === 'number' ? right.pageNum : 0;

    if (leftPage !== rightPage) {
      return leftPage - rightPage;
    }

    const leftY = typeof left.y === 'number' ? left.y : 0;
    const rightY = typeof right.y === 'number' ? right.y : 0;
    const yDiff = leftY - rightY;

    if (Math.abs(yDiff) > lineTolerance) {
      return yDiff;
    }

    const leftX = typeof left.x === 'number' ? left.x : 0;
    const rightX = typeof right.x === 'number' ? right.x : 0;
    return leftX - rightX;
  });
}

function getTargetAlignedItems(
  items: PdfFallbackTextItem[],
  target: string
): PdfFallbackTextItem[] {
  const normalizedTarget = normalizeForAlignment(target);
  let currentIndex = 0;
  const alignedItems: PdfFallbackTextItem[] = [];

  for (const item of items) {
    const normalizedItemText = normalizeForAlignment(item.text ?? '');

    if (!normalizedItemText) {
      continue;
    }

    const foundIndex = normalizedTarget.indexOf(
      normalizedItemText,
      currentIndex
    );

    if (foundIndex === -1) {
      continue;
    }

    alignedItems.push(item);
    currentIndex = foundIndex + normalizedItemText.length;

    if (currentIndex >= normalizedTarget.length) {
      break;
    }
  }

  return alignedItems;
}

function groupItemsIntoRows(
  items: PdfFallbackTextItem[]
): PdfFallbackTextItem[][] {
  const sortedItems = sortTextItemsReadingOrder(items);
  const rows: PdfFallbackTextItem[][] = [];

  for (const item of sortedItems) {
    const lastRow = rows[rows.length - 1];

    if (!lastRow) {
      rows.push([item]);
      continue;
    }

    const lastRowY = typeof lastRow[0]?.y === 'number' ? lastRow[0].y : 0;
    const itemY = typeof item.y === 'number' ? item.y : 0;
    const lastRowPageNumber =
      typeof lastRow[0]?.pageNum === 'number' ? lastRow[0].pageNum : 0;
    const itemPageNumber = typeof item.pageNum === 'number' ? item.pageNum : 0;

    if (
      lastRowPageNumber === itemPageNumber &&
      Math.abs(itemY - lastRowY) <= lineTolerance
    ) {
      lastRow.push(item);
      continue;
    }

    rows.push([item]);
  }

  return rows.map((row) => sortTextItemsReadingOrder(row));
}

function getOutsideBoundary(
  items: PdfFallbackTextItem[]
): PdfFallbackBoundary | null {
  if (items.length === 0) {
    return null;
  }

  const validItems = items.filter(
    (item) =>
      typeof item.x === 'number' &&
      typeof item.y === 'number' &&
      typeof item.width === 'number' &&
      typeof item.height === 'number'
  );

  if (validItems.length === 0) {
    return null;
  }

  const minX = Math.min(...validItems.map((item) => item.x as number));
  const minY = Math.min(...validItems.map((item) => item.y as number));
  const maxX = Math.max(
    ...validItems.map((item) => (item.x as number) + (item.width as number))
  );
  const maxY = Math.max(
    ...validItems.map((item) => (item.y as number) + (item.height as number))
  );

  return {
    height: maxY - minY,
    width: maxX - minX,
    x: minX,
    y: minY
  };
}

function isInsideBoundary(
  item: PdfFallbackTextItem,
  boundary: PdfFallbackBoundary
): boolean {
  if (
    typeof item.x !== 'number' ||
    typeof item.y !== 'number' ||
    typeof item.width !== 'number' ||
    typeof item.height !== 'number'
  ) {
    return false;
  }

  const itemRight = item.x + item.width;
  const itemBottom = item.y + item.height;
  const boundaryRight = boundary.x + boundary.width;
  const boundaryBottom = boundary.y + boundary.height;

  return (
    item.x >= boundary.x &&
    item.y >= boundary.y &&
    itemRight <= boundaryRight &&
    itemBottom <= boundaryBottom
  );
}

function createDimensionItems(
  items: PdfFallbackTextItem[]
): PdfFallbackDimensionItem[] {
  const rows = groupItemsIntoRows(items);
  const rowBlocks = rows
    .map((row) => {
      const boundary = getOutsideBoundary(row);
      const pageNumbers = normalizePageNumbers(row.map((item) => item.pageNum));

      if (!boundary || pageNumbers.length !== 1) {
        return null;
      }

      return {
        ...boundary,
        items: row,
        pageNumber: pageNumbers[0]
      };
    })
    .filter(
      (rowBlock): rowBlock is PdfFallbackDimensionItem => rowBlock !== null
    );
  const dimensionItems: PdfFallbackDimensionItem[] = [];

  for (const rowBlock of rowBlocks) {
    const previousBlock = dimensionItems[dimensionItems.length - 1];

    if (!previousBlock) {
      dimensionItems.push({
        ...rowBlock,
        items: [...rowBlock.items]
      });
      continue;
    }

    const sameHorizontalSpan =
      previousBlock.pageNumber === rowBlock.pageNumber &&
      Math.abs(previousBlock.x - rowBlock.x) <= lineTolerance &&
      Math.abs(previousBlock.width - rowBlock.width) <= lineTolerance;

    if (!sameHorizontalSpan) {
      dimensionItems.push({
        ...rowBlock,
        items: [...rowBlock.items]
      });
      continue;
    }

    const mergedBoundary = getOutsideBoundary([
      ...previousBlock.items,
      ...rowBlock.items
    ]);

    if (!mergedBoundary) {
      continue;
    }

    previousBlock.x = mergedBoundary.x;
    previousBlock.y = mergedBoundary.y;
    previousBlock.width = mergedBoundary.width;
    previousBlock.height = mergedBoundary.height;
    previousBlock.items = [...previousBlock.items, ...rowBlock.items];
  }

  return dimensionItems;
}

function resolvePageNumberFromItems(
  items: PdfFallbackTextItem[]
): number | null {
  const pageNumbers = normalizePageNumbers(items.map((item) => item.pageNum));

  return pageNumbers.length === 1 ? pageNumbers[0] : null;
}

function resolvePageNumbersFromItems(items: PdfFallbackTextItem[]): number[] {
  return normalizePageNumbers(items.map((item) => item.pageNum));
}

function itemMatchesPageNumber(
  item: PdfFallbackTextItem,
  pageNumber: number | null
) {
  return (
    pageNumber === null ||
    item.pageNum === undefined ||
    item.pageNum === pageNumber
  );
}

function itemMatchesPageNumbers(
  item: PdfFallbackTextItem,
  allowedPageNumbers: Set<number> | null
) {
  return (
    allowedPageNumbers === null ||
    item.pageNum === undefined ||
    allowedPageNumbers.has(item.pageNum)
  );
}

export function findFallbackDimensionItems(
  items: PdfFallbackTextItem[],
  largerString: string,
  options: {
    pageNumber?: number;
    pageNumbers?: number[];
  } = {}
): PdfFallbackSearchResult {
  console.log('items', items);
  const explicitPageNumbers = normalizePageNumbers([
    ...(options.pageNumbers ?? []),
    options.pageNumber
  ]);
  const allowedPageNumbers =
    explicitPageNumbers.length > 0 ? new Set(explicitPageNumbers) : null;
  const explicitPageNumber =
    explicitPageNumbers.length === 1 ? explicitPageNumbers[0] : null;
  const searchableItems = items?.filter((item) =>
    itemMatchesPageNumbers(item, allowedPageNumbers)
  );

  if (!largerString.trim() || items.length === 0) {
    return {
      boundary: null,
      dimensionItems: [],
      matchedItems: [],
      pageNumber:
        explicitPageNumber ?? resolvePageNumberFromItems(searchableItems),
      pageNumbers:
        explicitPageNumbers.length > 0
          ? explicitPageNumbers
          : resolvePageNumbersFromItems(searchableItems)
    };
  }

  const matches = findObjectsInLargerString(searchableItems, largerString, {
    getPageNum: (item) => item.pageNum,
    getText: (item) => item.text,
    threshold: 1
  });

  const bestCombination = findBestMatchCombination(matches, largerString, {
    getPageNum: (item) => item.pageNum,
    getText: (item) => item.text,
    getX: (item) => item.x,
    getY: (item) => item.y
  });

  const bestCombinationItemsSorted = sortTextItemsReadingOrder(
    bestCombination?.matches.map((match) => match.item) ?? []
  );
  console.log('best Combination items', bestCombinationItemsSorted);
  const bestCombinationPageNumbers = resolvePageNumbersFromItems(
    bestCombinationItemsSorted
  );
  const outsideBoundary =
    bestCombinationPageNumbers.length === 1
      ? getOutsideBoundary(bestCombinationItemsSorted)
      : null;
  const bestCombinationItemKeys = new Set(
    bestCombinationItemsSorted.map((item) => getItemKey(item))
  );
  const fallbackPageNumber =
    explicitPageNumber ??
    resolvePageNumberFromItems(bestCombinationItemsSorted) ??
    resolvePageNumberFromItems(searchableItems);
  const missedItemsInBoundary = outsideBoundary
    ? sortTextItemsReadingOrder(
        searchableItems.filter(
          (item) =>
            itemMatchesPageNumber(item, fallbackPageNumber) &&
            isInsideBoundary(item, outsideBoundary) &&
            !bestCombinationItemKeys.has(getItemKey(item))
        )
      )
    : [];
  const allItemsInBoundarySorted = sortTextItemsReadingOrder([
    ...bestCombinationItemsSorted,
    ...missedItemsInBoundary
  ]);
  const targetAlignedItemsSorted = getTargetAlignedItems(
    allItemsInBoundarySorted,
    largerString
  );
  const resolvedPageNumbers =
    explicitPageNumbers.length > 0
      ? explicitPageNumbers
      : resolvePageNumbersFromItems(targetAlignedItemsSorted).length > 0
        ? resolvePageNumbersFromItems(targetAlignedItemsSorted)
        : resolvePageNumbersFromItems(bestCombinationItemsSorted);
  const resolvedPageNumber =
    resolvedPageNumbers.length > 0
      ? resolvedPageNumbers[0]
      : fallbackPageNumber;

  return {
    boundary:
      resolvedPageNumbers.length === 1
        ? getOutsideBoundary(targetAlignedItemsSorted)
        : null,
    dimensionItems: createDimensionItems(targetAlignedItemsSorted),
    matchedItems: targetAlignedItemsSorted,
    pageNumber: resolvedPageNumber,
    pageNumbers: resolvedPageNumbers
  };
}
